import React from 'react';
import PropTypes from 'prop-types';
import ReactCurrencyInput from 'react-currency-input-field';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { defaultLocale } from 'helpers/supported-locales';

import { makeValidators } from '../helpers';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Label from '../Label/Label';

import './CurrencyInput.scss';

const { block, element } = bem('hook-form-currency-input');

const DECIMAL_LIMIT = 2;

const CurrencyInput = props => {
  const {
    className,
    currencyCode,
    disabled,
    label,
    locale = defaultLocale,
    name,
    noMargin,
    optional,
    placeholder: placeholderFromProps,
    required,
    showRequiredLabel,
    ...passThroughProps
  } = props;

  const intl = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const validators = makeValidators({ ...props }, intl);
  const hasError = Object.keys(errors).includes(name);

  let placeholder;
  if (placeholderFromProps) {
    placeholder = intl.formatMessage(placeholderFromProps);
  } else {
    placeholder = currencyCode ? intl.formatNumber(0, { style: 'currency', currency: currencyCode }) : '';
  }

  return (
    <div className={block(className, { noMargin })}>
      <Label
        name={name}
        message={label}
        optional={optional}
        required={required}
        showRequiredLabel={showRequiredLabel}
      />
      <div className={element('input-container', className, { hasError })}>
        <Controller
          name={name}
          control={control}
          rules={validators}
          render={({ field: { onChange } }) => (
            <ReactCurrencyInput
              className={element('input')}
              decimalsLimit={DECIMAL_LIMIT}
              disabled={disabled}
              intlConfig={{ locale, currency: currencyCode }}
              onValueChange={onChange}
              placeholder={placeholder}
              {...passThroughProps}
            />
          )}
        />
      </div>
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
};

CurrencyInput.propTypes = {
  className: PropTypes.string,
  currencyCode: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  label: intlMessage,
  locale: PropTypes.string,
  name: PropTypes.string.isRequired,
  noMargin: PropTypes.bool,
  optional: PropTypes.bool,
  placeholder: intlMessage,
  required: PropTypes.bool,
  showRequiredLabel: PropTypes.bool,
};

export default CurrencyInput;
